import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { getFinancialProfile } from '@/selectors/user.selectors'
import {
  useFetchResponse,
  useLoadedFlagValue,
  useNavigateWithPersistParams,
  useReselector,
  useScrollRef,
} from '@/utils/sharedHooks'
import { FEATURE_FLAG_KEYS } from '@/features/open-feature'
import { useViewerPlanSegment } from '@/features/user/settings/sections/plan-change/lib/helpers'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  SIGNUP_PATHS,
  PRACTICE_JOURNEY_SELECTIONS,
  practiceJourneyTitle,
} from '../helpers'
import { useFreeTrialEnabled } from '../useFreeTrialEnabled'
import { useAnalyticsView } from '@/features/amplitude'
import { selectCanOptIntoPreviousTaxYear } from '@/features/admin/annual-tax-details/annualTaxDetails.selector'
import {
  FETCH_ALL_ANNUAL_TAX_DETAILS_KEY,
  fetchAllAnnualTaxDetailsIfNeeded,
} from '@/features/admin/annual-tax-details/annualTaxDetails.slice'
import { TypographyStyles } from '@/styles'
import { Card, CardColors, Text } from '@/ui'
import { FreeTrialSignupBanner } from '../FreeTrialSignupBanner'
import { PlanTier } from '@/actions/settings/billingActions'
import {
  FETCH_PRODUCT_PERMISSIONS_KEY,
  getProductPermissions,
  ProductFeatureId,
} from '@/actions/permissionActions'
import {
  getFetchError,
  getIsFetching,
  selectIsFetchingForKeys,
} from '@/reducers/fetch'
import { TierSelection } from './TierSelection'
import { selectProductDescription } from '@/features/user/signup/selectors'
import { usePayrollSignupPromo } from '../payroll-signup-promo.helpers'
import { SignupPageLayout } from '../SignupPageLayout'
import { SignupFooter } from '../SignupFooter'
import {
  fetchSignupPlans,
  FETCH_SIGNUP_PLANS_KEY,
} from '../plans/signupPlans.actions'
import { recordChooserDecision } from '../plans/chooserDecision'
import { isChooserBypassCohort } from '../plans/chooserVisibility'
import { NO_MATCHING_PLANS_MESSAGE } from '../plans/noPlansMessage'
import {
  isPremiumTier,
  orderedCards,
  validateStoredTier,
} from '../plans/signupPlans'
import type { SignupCard } from '../plans/signupPlans.types'
import { DedicatedExpertSupport } from './DedicatedExpertSupport'
import { EVERYTHING_IN_ESSENTIAL_LINE } from './premiumCard.copy'

type ProductDescription = ReturnType<typeof selectProductDescription>
type ProductDescriptionSections = Parameters<
  typeof selectProductDescription
>[1]['sections']

const LITE_SECTIONS: ProductDescriptionSections = [
  'bookkeeping',
  'reporting',
  'taxes',
  'support',
]
const STANDARD_SECTIONS: ProductDescriptionSections = [
  'taxes',
  'scorp',
  'group',
  'support',
]
const FULL_SECTIONS: ProductDescriptionSections = [
  'bookkeeping',
  'reporting',
  ...STANDARD_SECTIONS,
]
const LITE_BASED_TIERS: readonly PlanTier[] = [
  PlanTier.essential,
  PlanTier.premium,
]

const sectionsFor = (
  isLite: boolean,
  listsEverything: boolean
): ProductDescriptionSections => {
  if (isLite) return LITE_SECTIONS
  return listsEverything ? FULL_SECTIONS : STANDARD_SECTIONS
}

/**
 * Per-tier "Includes:" copy. A tier absent here (Lite) renders no includes list at all.
 */
const CARD_EXTRAS: Partial<Record<PlanTier, { includesLine: string }>> = {
  [PlanTier.essential]: { includesLine: 'Everything in Lite Plan' },
  [PlanTier.premium]: { includesLine: 'Everything in Lite Plan' },
  [PlanTier.essentialPlus]: { includesLine: EVERYTHING_IN_ESSENTIAL_LINE },
  [PlanTier.premiumPlus]: { includesLine: EVERYTHING_IN_ESSENTIAL_LINE },
}

/** Strips any item from `details` that's already advertised on `baseDetails`, the card one rank below. */
const dedupeAgainstBase = (
  details: ProductDescription,
  baseDetails: ProductDescription | undefined
): ProductDescription => {
  if (!baseDetails || !details) return details
  const baseItemTexts = new Set(
    baseDetails
      .filter((section) => section.isVisible)
      .flatMap((section) => section.details.map((d) => d.text))
  )
  return details.map((section) => {
    const filtered = section.details.filter((d) => !baseItemTexts.has(d.text))
    return {
      ...section,
      details: filtered,
      isVisible: section.isVisible && filtered.length > 0,
    }
  })
}

const PlanCard = ({
  card,
  selection,
  setSelection,
  permissions,
  isLoading,
  copyFromTier,
  payrollPromoText,
  baseDetails,
  freeTrialEnabled,
  liteOffered,
}: {
  card: SignupCard
  selection: string | undefined
  setSelection: (val: string) => void
  permissions: ProductFeatureId[] | undefined
  isLoading: boolean
  copyFromTier: boolean
  payrollPromoText: string | undefined
  baseDetails: ProductDescription | undefined
  freeTrialEnabled: boolean
  liteOffered: boolean
}) => {
  const isLite = card.tier === PlanTier.lite
  // Without Lite in the lineup there is nothing to inherit, so the card lists everything itself.
  const listsEverything = LITE_BASED_TIERS.includes(card.tier) && !liteOffered
  const details = useReselector(selectProductDescription, {
    sections: sectionsFor(isLite, listsEverything),
    isLitePlanEnabled: true,
    permissions,
    tier: card.tier,
    copyFromTier,
    payrollPromoText,
  })

  // Every non-Lite card already advertises its base tier's "Includes:" line, so strip any
  // item from its sections that also appears on the card one rank below to avoid duplicates.
  const dedupedDetails = useMemo(
    () => (isLite ? details : dedupeAgainstBase(details, baseDetails)),
    [details, baseDetails, isLite]
  )

  const viewerPlanSegment = useViewerPlanSegment()
  const selectionConfig = PRACTICE_JOURNEY_SELECTIONS[card.tier]
  if (!selectionConfig) return null
  const title =
    card.tier === PlanTier.essential || card.tier === PlanTier.premium
      ? practiceJourneyTitle(card.tier, viewerPlanSegment)
      : selectionConfig.title

  const cardExtras = CARD_EXTRAS[card.tier]

  return (
    <TierSelection
      {...selectionConfig}
      title={title}
      setSelection={setSelection}
      selection={selection}
      details={dedupedDetails}
      isStarterPlan={isLite || listsEverything}
      includesLine={listsEverything ? undefined : cardExtras?.includesLine}
      expertSupport={
        isPremiumTier(card.tier) ? (
          <DedicatedExpertSupport isTrial={freeTrialEnabled} />
        ) : undefined
      }
      isLoading={isLoading}
    />
  )
}

export const CatalogChoosePlan = () => {
  const dispatch = useAppDispatch()

  const navigate = useNavigateWithPersistParams()
  const [loading, setLoading] = useState(false)
  const pageView = useAnalyticsView()
  const { scrollRef, scrollToRef } = useScrollRef({ autoScroll: true })
  const [searchParams] = useSearchParams()
  const freeTrialSearchParamValue = searchParams.get('trial')
  const canOptIntoPreviousYearTaxes = useReselector(
    selectCanOptIntoPreviousTaxYear
  )
  const { payrollPromoTextForTier } = usePayrollSignupPromo()
  const financialProfile = useReselector(getFinancialProfile)
  const bypassCohort = isChooserBypassCohort(financialProfile)

  // Missing metadata reads as not eligible, so a forward must wait for the request to settle.
  const annualTaxDetailsLoading = useReselector(
    getIsFetching,
    FETCH_ALL_ANNUAL_TAX_DETAILS_KEY
  )

  const plans = useFetchResponse(fetchSignupPlans)
  const cards = useMemo(
    () => orderedCards(plans ?? [], { excludeLite: bypassCohort }),
    [plans, bypassCohort]
  )
  const permissions = useFetchResponse(getProductPermissions, {
    productIds: cards.map((card) => card.productId),
  })

  const [planTier, setPlanTier] = useState<string | undefined>(
    localStorage.getItem('planTier') ?? undefined
  )

  // The stored value is read before the plan list loads, so it counts only once a
  // successful response contains it; until then, and after a failed request, nothing is selected.
  const selectedTier = plans ? validateStoredTier(planTier, cards) : undefined

  const isLoadingPlanData = useReselector(selectIsFetchingForKeys, [
    FETCH_PRODUCT_PERMISSIONS_KEY,
    FETCH_SIGNUP_PLANS_KEY,
  ])
  const fetchingPlansError = useReselector(
    getFetchError,
    FETCH_SIGNUP_PLANS_KEY
  )
  // A settled, successful list with nothing in it is not a loading state: say so and point to support.
  const noPlansAvailable =
    plans !== undefined && !isLoadingPlanData && cards.length === 0
  const selectedLitePlan = selectedTier === PlanTier.lite
  const freeTrialEnabled = useFreeTrialEnabled(freeTrialSearchParamValue)
  const copyFromTier = Boolean(
    useLoadedFlagValue(FEATURE_FLAG_KEYS.planCopyFromTier, false)
  )

  // Each tier's "base" is the card one rank below it in the lineup, whose visible items
  // get stripped out of the tier above's list to avoid duplicates. Only tiers that serve
  // as somebody else's base need computing here; the rest dedupe inside their own PlanCard.
  const liteCard = cards.find((card) => card.tier === PlanTier.lite)
  const liteDetails = useReselector(selectProductDescription, {
    sections: LITE_SECTIONS,
    isLitePlanEnabled: true,
    permissions: liteCard ? permissions?.[liteCard.productId] : undefined,
    tier: PlanTier.lite,
    copyFromTier,
    payrollPromoText: payrollPromoTextForTier(PlanTier.lite),
  })

  const essentialCard = cards.find((card) => card.tier === PlanTier.essential)
  const essentialDetails = useReselector(selectProductDescription, {
    sections: STANDARD_SECTIONS,
    isLitePlanEnabled: true,
    permissions: essentialCard
      ? permissions?.[essentialCard.productId]
      : undefined,
    tier: PlanTier.essential,
    copyFromTier,
    payrollPromoText: payrollPromoTextForTier(PlanTier.essential),
  })

  const premiumCard = cards.find((card) => card.tier === PlanTier.premium)
  const premiumDetails = useReselector(selectProductDescription, {
    sections: STANDARD_SECTIONS,
    isLitePlanEnabled: true,
    permissions: premiumCard ? permissions?.[premiumCard.productId] : undefined,
    tier: PlanTier.premium,
    copyFromTier,
    payrollPromoText: payrollPromoTextForTier(PlanTier.premium),
  })

  const baseDetailsByTier: Partial<Record<PlanTier, ProductDescription>> = {
    [PlanTier.essential]: liteCard ? liteDetails : undefined,
    [PlanTier.essentialPlus]: essentialCard ? essentialDetails : undefined,
    [PlanTier.premium]: liteCard ? liteDetails : undefined,
    [PlanTier.premiumPlus]: premiumCard ? premiumDetails : undefined,
  }

  useEffect(() => {
    pageView('sign up practice journey')
  }, [pageView])

  useEffect(() => {
    scrollToRef()
  }, [scrollToRef])

  useEffect(() => {
    dispatch(fetchAllAnnualTaxDetailsIfNeeded())
  }, [dispatch])

  useEffect(() => {
    // An S-corp or group practice offered only one tier (no Premium+ yet) skips the
    // chooser and goes straight to payment with that tier preselected.
    if (plans === undefined || annualTaxDetailsLoading) return
    if (bypassCohort && cards.length === 1) {
      localStorage.setItem('planTier', cards[0].tier)
      recordChooserDecision(financialProfile, false)
      // The same prior-year-tax decision as a normal submit, replacing this chooser entry
      // so Back does not land on a page that only forwards again.
      navigate(
        canOptIntoPreviousYearTaxes
          ? SIGNUP_PATHS.previousYearTax
          : SIGNUP_PATHS.payment,
        undefined,
        { replace: true }
      )
    }
  }, [
    plans,
    annualTaxDetailsLoading,
    bypassCohort,
    cards,
    navigate,
    canOptIntoPreviousYearTaxes,
    financialProfile,
  ])

  useEffect(() => {
    // Back and the header read the recorded routing, which an earlier one-plan visit may have
    // left as skipped; two or more cards on screen make it shown.
    if (plans !== undefined && bypassCohort && cards.length >= 2) {
      recordChooserDecision(financialProfile, true)
    }
  }, [plans, bypassCohort, cards.length, financialProfile])

  const handleSubmit = () => {
    if (annualTaxDetailsLoading) return
    setLoading(true)
    let res: boolean | undefined
    if (selectedTier) {
      localStorage.setItem('planTier', selectedTier)
      res = true
    }
    setLoading(false)

    if (res) {
      // Heard Lite skips previous-year-tax step (no annual tax filing).
      // Trial codes do not skip it — they see the same steps as a paid signup.
      if (selectedLitePlan) {
        navigate(SIGNUP_PATHS.payment)
      } else {
        if (canOptIntoPreviousYearTaxes) {
          navigate(SIGNUP_PATHS.previousYearTax)
        } else {
          navigate(SIGNUP_PATHS.payment)
        }
      }
    }
  }

  const planCards = () => {
    if (fetchingPlansError) {
      return (
        <Card color={CardColors.LEVEL_0}>
          <Text variant={TypographyStyles.H2}>Missing information</Text>
          <Text>{fetchingPlansError.message}</Text>
        </Card>
      )
    }
    if (noPlansAvailable) {
      return <Text>{NO_MATCHING_PLANS_MESSAGE}</Text>
    }
    return cards.map((card) => (
      <PlanCard
        key={card.tier}
        card={card}
        selection={selectedTier}
        setSelection={setPlanTier}
        permissions={permissions?.[card.productId]}
        isLoading={isLoadingPlanData}
        copyFromTier={copyFromTier}
        payrollPromoText={payrollPromoTextForTier(card.tier)}
        baseDetails={baseDetailsByTier[card.tier]}
        freeTrialEnabled={freeTrialEnabled}
        liteOffered={Boolean(liteCard)}
      />
    ))
  }

  return (
    <SignupPageLayout
      maxWidth="wide"
      footer={
        <SignupFooter
          onBack={() => navigate(SIGNUP_PATHS.aboutYourPractice)}
          onContinue={handleSubmit}
          continueDisabled={!selectedTier || loading || annualTaxDetailsLoading}
          continueLoading={loading}
        />
      }
    >
      <div className="flex flex-col space-y-6">
        <span ref={scrollRef} />

        {freeTrialEnabled && <FreeTrialSignupBanner />}

        <Text variant={TypographyStyles.DISPLAY_MD}>
          Where are you in your private practice journey?
        </Text>

        <Text variant={TypographyStyles.BODY_MD}>
          Choose the option that is closest to your current practice.
        </Text>

        <div className="flex flex-col md:flex-row gap-6">{planCards()}</div>
      </div>
    </SignupPageLayout>
  )
}
